import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PermissionService } from 'src/app/services/permission.service';
import { portalCommands } from 'src/app/utils/portal-path';
import * as L from 'leaflet';
import * as QRCode from 'qrcode';
import { StudentsService } from 'src/app/services/students.service';
import { ClassesService } from 'src/app/services/classes.service';
import { WeeksService, Week } from 'src/app/services/weeks.service';
import { WeeklyPlansService } from 'src/app/services/weekly-plans.service';
import { LanguageService } from 'src/app/services/language.service';
import {
  DEFAULT_DEPARTMENT,
  DEFAULT_SCHOOL,
  PLAN_DAYS,
  PLAN_ROWS,
  cellsToGrid,
  teacherDisplayName,
  weekLabel,
  WeeklyPlan,
} from 'src/app/main/weekly-plans/weekly-plans.models';
import { User } from 'src/app/main/settings/users/users.component';

const markerIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

@Component({
  selector: 'app-student-profile',
  templateUrl: './student-profile.component.html',
  styleUrls: ['./student-profile.component.css']
})
export class StudentProfileComponent implements AfterViewInit, OnDestroy {
  @ViewChild('mapHost') mapHost?: ElementRef<HTMLDivElement>;

  student: any = null;
  loading = true;
  errorMessage = '';
  photoLightboxOpen = false;
  photoFailed = false;
  qrDataUrl = '';
  activeTab: 'overview' | string = 'overview';

  readonly tabs: { id: string; labelKey: string; icon: string }[] = [
    { id: 'overview', labelKey: 'STUDENTS.TAB_OVERVIEW', icon: 'person' },
    { id: 'teachers', labelKey: 'STUDENTS.TAB_TEACHERS', icon: 'groups' },
    { id: 'subjects', labelKey: 'STUDENTS.TAB_SUBJECTS', icon: 'menu_book' },
    { id: 'weekly-plan', labelKey: 'STUDENTS.TAB_WEEKLY_PLAN', icon: 'calendar_view_week' },
  ];

  classTeachers: User[] = [];
  classSubjects: { id: string; name: string; teachers: User[] }[] = [];
  weeks: Week[] = [];
  selectedWeekOrdinal = 1;
  weeklyPlan: WeeklyPlan | null = null;
  planTables: {
    subjectId: string;
    subjectName: string;
    teacherName: string;
    grid: string[][];
  }[] = [];
  relatedLoading = false;
  planLoading = false;
  readonly planDays = PLAN_DAYS;
  readonly planRows = PLAN_ROWS;

  private map?: L.Map;
  private marker?: L.Marker;
  private mapInitAttempts = 0;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private studentsService: StudentsService,
    private classesService: ClassesService,
    private weeksService: WeeksService,
    private weeklyPlansService: WeeklyPlansService,
    public languageService: LanguageService,
    public permissions: PermissionService,
  ) {}

  async ngOnInit() {
    this.route.queryParamMap.subscribe(params => {
      const tab = params.get('tab');
      if (tab && this.tabs.some(item => item.id === tab)) {
        this.activeTab = tab;
      }
    });
    this.route.paramMap.subscribe(async params => {
      const id = params.get('id');
      if (!id) {
        this.router.navigate(portalCommands(this.router, 'settings', 'students'));
        return;
      }
      await this.loadStudent(id);
    });
  }

  ngAfterViewInit(): void {
    this.queueMapInit();
  }

  ngOnDestroy(): void {
    this.destroyMap();
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    if (this.photoLightboxOpen) {
      this.closePhoto();
    }
  }

  async loadStudent(id: string) {
    this.loading = true;
    this.errorMessage = '';
    this.photoLightboxOpen = false;
    this.photoFailed = false;
    this.qrDataUrl = '';
    this.destroyMap();
    try {
      this.student = await this.studentsService.getById(id);
      if (!this.student) {
        this.errorMessage = 'STUDENTS.NOT_FOUND';
      } else {
        await this.refreshQrCode();
      }
    } catch {
      this.errorMessage = 'STUDENTS.LOAD_FAILED';
      this.student = null;
    } finally {
      this.loading = false;
      this.queueMapInit();
      void this.loadRelated();
    }
  }

  private async refreshQrCode() {
    const code = String(this.student?.qrCode || '').trim();
    if (!code) {
      this.qrDataUrl = '';
      return;
    }
    try {
      this.qrDataUrl = await QRCode.toDataURL(code, {
        width: 168,
        margin: 1,
        errorCorrectionLevel: 'M',
        color: { dark: '#173654', light: '#ffffff' },
      });
    } catch {
      this.qrDataUrl = '';
    }
  }

  get studentsLink(): string[] {
    return portalCommands(this.router, 'settings', 'students');
  }

  goBack() {
    this.router.navigate(portalCommands(this.router, 'settings', 'students'));
  }

  onEdit() {
    if (!this.student?.id) {
      return;
    }
    this.router.navigate(portalCommands(this.router, 'settings', 'students', this.student.id, 'edit'));
  }

  setTab(tabId: string) {
    if (this.activeTab === tabId || !this.tabs.some(tab => tab.id === tabId)) {
      return;
    }
    this.activeTab = tabId;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: tabId },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    if (tabId === 'overview') {
      this.destroyMap();
      this.queueMapInit();
    }
    if (tabId === 'weekly-plan' && !this.weeklyPlan) {
      void this.loadWeeklyPlan();
    }
  }

  get classId(): string {
    return this.student?.class?.id || '';
  }

  teacherName(user: User): string {
    return teacherDisplayName(user) || user.username || '—';
  }

  teacherSubjects(user: User): string {
    return (user.subjects || []).map(subject => subject.name).filter(Boolean).join(' · ') || '—';
  }

  subjectTeachersLabel(subject: { teachers: User[] }): string {
    return subject.teachers.map(teacher => this.teacherName(teacher)).join(' · ');
  }

  get weekChipLabel(): string {
    const week = this.weeks.find(item => item.ordinal === this.selectedWeekOrdinal);
    return week?.name || weekLabel(this.selectedWeekOrdinal);
  }

  get planSchoolName(): string {
    return this.weeklyPlan?.schoolName || DEFAULT_SCHOOL;
  }

  get planDepartmentName(): string {
    return this.weeklyPlan?.departmentName || DEFAULT_DEPARTMENT;
  }

  get planClassName(): string {
    return this.student?.class?.name || this.weeklyPlan?.class?.name || '';
  }

  fmtDisplayDate(value?: string): string {
    if (!value) {
      return '—';
    }
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return date.toLocaleDateString('ar-EG', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  cellContent(grid: string[][], rowIndex: number, dayIndex: number): string {
    return grid?.[rowIndex]?.[dayIndex] || '';
  }

  async loadRelated() {
    const classId = this.classId;
    this.classTeachers = [];
    this.classSubjects = [];
    this.weeklyPlan = null;
    this.planTables = [];
    if (!classId) {
      return;
    }

    this.relatedLoading = true;
    try {
      const [classDoc, weeks] = await Promise.all([
        this.classesService.getById(classId),
        this.weeksService.list(),
      ]);
      this.classTeachers = classDoc?.teachers || this.student?.class?.teachers || [];
      const bySubject = new Map<string, { id: string; name: string; teachers: User[] }>();
      for (const teacher of this.classTeachers) {
        for (const subject of teacher.subjects || []) {
          if (!subject?.id) {
            continue;
          }
          if (!bySubject.has(subject.id)) {
            bySubject.set(subject.id, { id: subject.id, name: subject.name, teachers: [] });
          }
          bySubject.get(subject.id)!.teachers.push(teacher);
        }
      }
      this.classSubjects = [...bySubject.values()].sort((a, b) => a.name.localeCompare(b.name, 'ar'));
      this.weeks = (weeks || []).slice().sort((a, b) => a.ordinal - b.ordinal);
      const active = this.weeks.find(week => week.active) || this.weeks[0];
      this.selectedWeekOrdinal = active?.ordinal || 1;
      await this.loadWeeklyPlan();
    } catch {
      this.classTeachers = [];
      this.classSubjects = [];
    } finally {
      this.relatedLoading = false;
    }
  }

  async loadWeeklyPlan() {
    if (!this.classId || !this.selectedWeekOrdinal) {
      this.weeklyPlan = null;
      this.planTables = [];
      return;
    }
    this.planLoading = true;
    try {
      this.weeklyPlan = await this.weeklyPlansService.getByClassAndWeek(this.classId, this.selectedWeekOrdinal);
      this.planTables = (this.weeklyPlan?.entries || []).map(entry => ({
        subjectId: entry.subject?.id || '',
        subjectName: entry.subject?.name || '',
        teacherName: teacherDisplayName(entry.teacher),
        grid: cellsToGrid(entry.cells || []),
      }));
    } catch {
      this.weeklyPlan = null;
      this.planTables = [];
    } finally {
      this.planLoading = false;
    }
  }

  async onWeekChange(ordinal: string | number) {
    this.selectedWeekOrdinal = Number(ordinal);
    await this.loadWeeklyPlan();
  }

  openTeacher(user: User) {
    if (!user?.id || !this.permissions.canView('users')) {
      return;
    }
    this.router.navigate(portalCommands(this.router, 'settings', 'users', user.id));
  }

  openParent(link: any) {
    const parentId = link?.parent?.id;
    if (!parentId || !this.permissions.canView('parents')) {
      return;
    }
    this.router.navigate(portalCommands(this.router, 'settings', 'parents', parentId));
  }

  openPhoto() {
    if (this.hasPhoto) {
      this.photoLightboxOpen = true;
    }
  }

  closePhoto() {
    this.photoLightboxOpen = false;
  }

  onPhotoError() {
    this.photoFailed = true;
    this.photoLightboxOpen = false;
  }

  get hasPhoto(): boolean {
    return !!this.student?.photo && !this.photoFailed;
  }

  get nameAr(): string {
    if (!this.student) {
      return '';
    }
    return `${this.student.firstNameAr || ''} ${this.student.lastNameAr || ''}`.trim();
  }

  get nameEn(): string {
    if (!this.student) {
      return '';
    }
    return `${this.student.firstNameEn || ''} ${this.student.lastNameEn || ''}`.trim();
  }

  get initials(): string {
    const parts = this.nameAr.split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last) || 'ط';
  }

  get statusKey(): string {
    return `STUDENTS.STATUS_${(this.student?.status || 'active').toUpperCase()}`;
  }

  get age(): number | null {
    const raw = this.student?.dateOfBirth;
    if (!raw) {
      return null;
    }
    const birth = new Date(raw);
    if (Number.isNaN(birth.getTime())) {
      return null;
    }
    const now = new Date();
    let years = now.getFullYear() - birth.getFullYear();
    const monthDiff = now.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birth.getDate())) {
      years -= 1;
    }
    return years >= 0 && years < 130 ? years : null;
  }

  get emergencyContact(): any | null {
    const links = this.parents;
    if (!links.length) {
      return null;
    }
    return links.find((link: any) => link.isPrimaryEmergency) || links[0];
  }

  get emergencyPhone(): string {
    const phones = this.emergencyContact?.parent?.phones || [];
    const primary = phones.find((phone: any) => phone.isPrimary) || phones[0];
    return primary?.value || '';
  }

  get emergencyContactName(): string {
    if (!this.emergencyContact) {
      return '—';
    }
    return this.languageService.current === 'ar'
      ? this.parentNameAr(this.emergencyContact)
      : (this.parentNameEn(this.emergencyContact) || this.parentNameAr(this.emergencyContact));
  }

  parentInitial(link: any): string {
    return this.parentNameAr(link).replace(/\s+/g, '').slice(0, 1) || 'و';
  }

  get sexKey(): string {
    if (!this.student?.sex) {
      return '';
    }
    return `STUDENTS.SEX_${String(this.student.sex).toUpperCase()}`;
  }

  get parents(): any[] {
    return this.student?.parents || [];
  }

  get phones(): any[] {
    return this.student?.phones || [];
  }

  get emails(): any[] {
    return this.student?.emails || [];
  }

  get hasAddress(): boolean {
    const a = this.student?.address;
    if (!a) {
      return false;
    }
    return !!(a.line1 || a.line2 || a.building || a.flat || a.emirate || a.area || a.landmark || this.hasCoordinates);
  }

  get hasCoordinates(): boolean {
    const coords = this.student?.address?.coordinates;
    return coords?.lat != null && coords?.lng != null;
  }

  get mapsLink(): string {
    if (!this.hasCoordinates) {
      return '';
    }
    const { lat, lng } = this.student.address.coordinates;
    return `https://www.google.com/maps?q=${lat},${lng}`;
  }

  valueOrDash(value: unknown): string {
    if (value == null) {
      return '—';
    }
    const text = String(value).trim();
    return text || '—';
  }

  bilingualLabel(item: any): string {
    if (!item) {
      return '';
    }
    const isAr = this.languageService.current === 'ar';
    if (item.nameAr || item.nameEn) {
      return isAr ? (item.nameAr || item.nameEn || '') : (item.nameEn || item.nameAr || '');
    }
    return isAr
      ? `${item.firstNameAr || ''} ${item.lastNameAr || ''}`.trim()
      : `${item.firstNameEn || ''} ${item.lastNameEn || ''}`.trim();
  }

  parentNameAr(link: any): string {
    const p = link?.parent;
    if (!p) {
      return '—';
    }
    return `${p.firstNameAr || ''} ${p.lastNameAr || ''}`.trim() || '—';
  }

  parentNameEn(link: any): string {
    const p = link?.parent;
    if (!p) {
      return '';
    }
    return `${p.firstNameEn || ''} ${p.lastNameEn || ''}`.trim();
  }

  parentPhones(link: any): any[] {
    return link?.parent?.phones || [];
  }

  parentEmails(link: any): any[] {
    return link?.parent?.emails || [];
  }

  relationKey(relation?: string): string {
    return `STUDENTS.RELATION_${(relation || 'guardian').toUpperCase()}`;
  }

  labelKey(label?: string): string {
    return `STUDENTS.LABEL_${(label || 'mobile').toUpperCase()}`;
  }

  private queueMapInit() {
    setTimeout(() => this.initMap(), 0);
  }

  private initMap() {
    if (this.loading || this.activeTab !== 'overview' || !this.hasCoordinates || this.map || !this.mapHost?.nativeElement) {
      if (!this.loading && this.activeTab === 'overview' && this.hasCoordinates && !this.map && this.mapInitAttempts < 10) {
        this.mapInitAttempts += 1;
        setTimeout(() => this.initMap(), 50);
      }
      return;
    }

    this.mapInitAttempts = 0;
    const { lat, lng } = this.student.address.coordinates;
    const center: L.LatLngExpression = [Number(lat), Number(lng)];

    this.map = L.map(this.mapHost.nativeElement, {
      center,
      zoom: 15,
      zoomControl: true,
      attributionControl: true,
      scrollWheelZoom: false,
      dragging: true,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap',
    }).addTo(this.map);

    this.marker = L.marker(center, { icon: markerIcon, interactive: false }).addTo(this.map);
    setTimeout(() => this.map?.invalidateSize(), 80);
  }

  private destroyMap() {
    this.map?.remove();
    this.map = undefined;
    this.marker = undefined;
    this.mapInitAttempts = 0;
  }
}
